import React, { useEffect, useState } from 'react';
import { useUser } from './User';
import {
  getToolModelList,
  toolAdminAddToolToBlockList,
  toolAdminGetBlockedToolList,
  toolAdminGetRegisteredToolList,
  toolAdminGetToolCertList,
  toolAdminRemoveToolFromBlockList,
} from '../libs/db-lib';
import { mergeToolLists } from '../libs/utils';

export interface ToolsInterface {
  isLoadingTools: boolean;
  toolModels: any[] | null;
  toolsData: any[] | null;
  refreshToolsData(): any;
  blockTool(tool: any): any;
  unblockTool(tool: any): any;
}

export const ToolsContext = React.createContext<ToolsInterface>({
  isLoadingTools: true,
  toolModels: null,
  toolsData: null,
  refreshToolsData: () => null,
  blockTool: (tool) => null,
  unblockTool: (tool) => null,
});

export const useTools = () => {
  return React.useContext(ToolsContext);
};

type ToolsProviderType = {
  children: JSX.Element;
};

export function ToolsProvider({ children }: ToolsProviderType) {
  const { user } = useUser();

  const [isLoadingTools, setIsLoadingTools] = useState<boolean>(true);
  const [toolModels, setToolModels] = useState<any[]>([]);
  const [toolsData, setToolsData] = useState<any[]>([]);

  useEffect(() => {
    if (user && user?.userType === 'TOOL_ADMIN') {
      refreshToolsData();
    } else {
      setToolsData([]);
    }
  }, [user]);

  const refreshToolsData = async () => {
    setIsLoadingTools(true);

    const responses = await Promise.all([
      getToolModels(),
      getToolCerts(),
      getRegisteredTools(),
      getBlockedTools(),
    ]);

    setToolModels(responses[0]);
    const toolCerts = responses[1];
    const registeredTools = responses[2];
    const blockedTools = responses[3];

    if (
      Array.isArray(toolCerts) &&
      Array.isArray(registeredTools) &&
      Array.isArray(blockedTools)
    ) {
      setToolsData(
        mergeToolLists(toolCerts, registeredTools, blockedTools)?.sort(
          (a, b) => {
            return (a?.blocked ?? false) === (b?.blocked ?? false)
              ? 0
              : a?.blocked ?? false
              ? -1
              : 1; // Sort by blocked tools
          }
        )
      );
    }

    setIsLoadingTools(false);
  };

  const getToolModels = async () => {
    const response = await getToolModelList({
      userID: user?.userID,
      userType: user?.userType,
      toolManufacturerUUID: user?.vendorAutoAuthUUID,
    });

    if (!response.error) {
      return (response as any)?.map((model: any) => model?.toolModel);
    } else {
      console.log('Failed to get tool models', response.error);
      return [];
    }
  };

  const getToolCerts = async () => {
    const response = await toolAdminGetToolCertList({
      userID: user?.userID,
      userType: user?.userType,
      toolManufacturerUUID: user?.vendorAutoAuthUUID,
    });

    if (!response.error) {
      return response;
    } else {
      console.log('Failed to get tool certs', response.error);
      return [];
    }
  };

  const getRegisteredTools = async () => {
    const response = await toolAdminGetRegisteredToolList({
      userID: user?.userID,
      userType: user?.userType,
      toolManufacturerUUID: user?.vendorAutoAuthUUID,
    });

    if (!response.error) {
      return response;
    } else {
      console.log('Failed to get registered tools', response.error);
      return [];
    }
  };

  const getBlockedTools = async () => {
    const response = await toolAdminGetBlockedToolList({
      userID: user?.userID,
      userType: user?.userType,
      toolManufacturerUUID: user?.vendorAutoAuthUUID,
    });

    if (!response.error) {
      return response;
    } else {
      console.log('Failed to get blocked tools', response.error);
      return [];
    }
  };

  const blockTool = async (tool: any) => {
    const response = await toolAdminAddToolToBlockList({
      userID: user?.userID,
      toolManufacturer: tool?.toolManufacturer,
      toolManufacturerUUID: user?.vendorAutoAuthUUID,
      toolModel: tool?.toolModel,
      toolModelUUID: tool?.toolModelUUID,
      toolSerial: tool?.toolSerial,
    });

    if (!response.error) {
      setToolsData((prev) =>
        prev?.map((t) =>
          t?.toolModelUUID === tool?.toolModelUUID &&
          t?.toolSerial === tool?.toolSerial
            ? {
                ...t,
                previouslyRegistered:
                  (t?.isRegistered ?? 'NO').toLowerCase() === 'yes',
                isRegistered: 'NO',
                blocked: true,
              }
            : t
        )
      );
    }

    return response;
  };

  const unblockTool = async (tool: any) => {
    const response = await toolAdminRemoveToolFromBlockList({
      userID: user?.userID,
      toolManufacturerUUID: user?.vendorAutoAuthUUID,
      toolModelUUID: tool?.toolModelUUID,
      toolSerial: tool?.toolSerial,
    });

    if (!response.error) {
      if (tool?.previouslyRegistered !== true) {
        // Could be undefined
        setToolsData((prev) =>
          prev?.map((t) =>
            t?.toolModelUUID === tool?.toolModelUUID &&
            t?.toolSerial === tool?.toolSerial
              ? { ...t, blocked: false }
              : t
          )
        );
      } else {
        setToolsData((prev) =>
          prev?.filter(
            (t) =>
              t?.toolModelUUID !== tool?.toolModelUUID ||
              t?.toolSerial !== tool?.toolSerial
          )
        ); // If the tool was previously registered, filter it out.
      }
    }

    return response;
  };

  const provider = {
    isLoadingTools,
    toolModels,
    toolsData,
    refreshToolsData,
    blockTool,
    unblockTool,
  };

  return (
    <ToolsContext.Provider value={provider}>{children}</ToolsContext.Provider>
  );
}
